import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { ArrowLeft, Mail, Phone, FolderKanban, Award, Ban, CheckCircle2, Trash2, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { startImpersonation } from "@/lib/impersonate";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { MessageThread } from "@/components/ProjectMessages";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("tr-TR") : "-");
const fmtPrice = (n) => new Intl.NumberFormat("tr-TR").format(n);

const projectInfo = (p) =>
  [p.category || p.type, p.location || (p.region ? `${p.region}${p.stand_no ? " · " + p.stand_no : ""}` : null)]
    .filter(Boolean)
    .join(" · ") || "-";

export default function AdminCustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const fullAdmin = (authUser?.permissions || ["all"]).includes("all");
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const toggleActive = async () => {
    setBusy(true);
    try {
      const next = c.is_active === false;
      await api.patch(`/customers/${id}`, { is_active: next });
      setC((p) => ({ ...p, is_active: next }));
      toast.success(next ? "Müşteri aktifleştirildi" : "Müşteri pasifleştirildi — artık giriş yapamaz");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteCustomer = async () => {
    setBusy(true);
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Müşteri ve tüm projeleri silindi");
      navigate("/admin/musteriler");
    } catch (err) {
      toast.error(apiError(err));
      setBusy(false);
    }
  };

  useEffect(() => {
    api
      .get(`/customers/${id}`)
      .then((r) => setC(r.data))
      .catch((err) => toast.error(apiError(err)))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-slate-400 text-sm">Yükleniyor...</div>;
  if (!c) return <div className="text-slate-500 text-sm">Müşteri bulunamadı.</div>;

  return (
    <div className="space-y-8" data-testid="admin-customer-detail-page">
      <Link to="/admin/musteriler" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200" data-testid="back-to-customers">
        <ArrowLeft size={15} /> Müşterilere Dön
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Müşteri Detayı</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1 flex items-center gap-3" data-testid="customer-company-name">
            {c.company_name}
            {c.is_active === false && (
              <span className="text-[10px] uppercase tracking-wider bg-red-50 text-red-700 border border-red-200 px-2 py-1 font-semibold" data-testid="customer-inactive-badge">
                Pasif
              </span>
            )}
          </h1>
          <div className="text-sm text-slate-600 mt-2">{c.contact_name}</div>
          <div className="flex items-center gap-3 mt-4">
            {fullAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => { try { await startImpersonation(id); } catch (e) { toast.error(apiError(e)); } }}
                data-testid="impersonate-customer-button"
                className="rounded-none border-indigo-300 text-indigo-700 hover:bg-indigo-50 gap-2 transition-colors duration-200"
              >
                <UserCog size={14} /> Bu Hesaba Geç
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={toggleActive}
              data-testid="toggle-customer-active-button"
              className="rounded-none border-slate-300 hover:bg-slate-100 gap-2 transition-colors duration-200"
            >
              {c.is_active === false ? <><CheckCircle2 size={14} className="text-emerald-600" /> Aktifleştir</> : <><Ban size={14} /> Pasifleştir</>}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  data-testid="delete-customer-button"
                  className="rounded-none border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 gap-2 transition-colors duration-200"
                >
                  <Trash2 size={14} /> Sil
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-none bg-white border-slate-200">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-heading tracking-tight">Müşteriyi sil</AlertDialogTitle>
                  <AlertDialogDescription>
                    <strong>{c.company_name}</strong> hesabı, <strong>{c.projects.length} projesi</strong>, bağlı talepleri, gelen teklifleri ve mesajları kalıcı olarak silinecek. Bu işlem geri alınamaz.
                    Verileri korumak istiyorsanız silmek yerine <strong>Pasifleştir</strong> seçeneğini kullanabilirsiniz.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-none" data-testid="cancel-delete-customer">Vazgeç</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteCustomer} data-testid="confirm-delete-customer" className="rounded-none bg-red-600 hover:bg-red-700 text-white">
                    Kalıcı Olarak Sil
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        <div className="text-sm space-y-2">
          {c.contact_email && (
            <a href={`mailto:${c.contact_email}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors duration-200" data-testid="customer-email">
              <Mail size={14} /> {c.contact_email}
            </a>
          )}
          {c.contact_phone && (
            <a href={`tel:${c.contact_phone}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors duration-200" data-testid="customer-phone">
              <Phone size={14} /> {c.contact_phone}
            </a>
          )}
          <div className="text-xs text-slate-400">Kayıt: {fmtDate(c.created_at)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Proje", value: c.projects.length },
          { label: "Talep", value: c.projects.filter((p) => p.request_id).length },
          { label: "Gelen Teklif", value: c.projects.reduce((a, p) => a + p.offer_count, 0) },
          { label: "Kabul Edilen", value: c.projects.filter((p) => p.winner).length },
        ].map((k) => (
          <div key={k.label} className="border border-slate-200 bg-white p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{k.label}</div>
            <div className="font-heading text-3xl font-extrabold tracking-tighter mt-1">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="border border-slate-200 bg-white">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2 text-sm font-semibold">
          <FolderKanban size={15} /> Projeler ve Talepler
        </div>
        {c.projects.length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-500" data-testid="no-customer-projects">Bu müşterinin henüz projesi yok.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-medium">Proje</th>
                  <th className="px-5 py-3 font-medium">Detay</th>
                  <th className="px-5 py-3 font-medium">Son Tarih</th>
                  <th className="px-5 py-3 font-medium text-center">Teklif</th>
                  <th className="px-5 py-3 font-medium">Sonuç</th>
                  <th className="px-5 py-3 font-medium">Oluşturma</th>
                </tr>
              </thead>
              <tbody>
                {c.projects.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => navigate(p.request_id ? `/admin/teklifler/${p.request_id}` : `/admin/projeler/${p.id}`)}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer transition-colors duration-150"
                    data-testid={`customer-project-row-${p.id}`}
                  >
                    <td className="px-5 py-4 font-semibold text-slate-900">{p.name}</td>
                    <td className="px-5 py-4 text-slate-600">{projectInfo(p)}</td>
                    <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{p.deadline ? fmtDate(p.deadline) : "-"}</td>
                    <td className="px-5 py-4 text-center">{p.request_id ? p.offer_count : <span className="text-slate-400 text-xs">Talep yok</span>}</td>
                    <td className="px-5 py-4">
                      {p.winner ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1">
                          <Award size={12} /> {p.winner.company} · {fmtPrice(p.winner.price)} {p.winner.currency}
                        </span>
                      ) : p.request_id ? (
                        <span className="text-xs text-slate-500">Değerlendirmede</span>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-slate-500 whitespace-nowrap">{fmtDate(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="max-w-3xl">
        <MessageThread
          endpoint={`/customers/${id}/messages`}
          myRole="admin"
          title="Müşteri ile Mesajlaşma"
          emptyText="Henüz mesaj yok. Müşteriye ilk mesajı gönderin — panelinde anında görünür."
          placeholder="Müşteriye mesajınızı yazın..."
        />
      </div>
    </div>
  );
}
